import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  differenceInCalendarDays,
  parseISO,
} from 'date-fns';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  X,
  Lock,
  Trash2,
} from 'lucide-react';
import { getVenueById } from '../services/venueService';
import { getVenueCourts, blockCourt, deleteBlockedSlot, getBlockedSlots } from '../services/courtService';
import { getVenueBookings } from '../services/bookingService';

export default function AvailabilityCalendar() {
  const { id: venueId } = useParams();
  const navigate = useNavigate();

  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [venue, setVenue] = useState(null);
  const [courts, setCourts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [removingBlockId, setRemovingBlockId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [blockModal, setBlockModal] = useState(null);
  const [blocking, setBlocking] = useState(false);
  const [blockForm, setBlockForm] = useState({
    courtId: '',
    date: '',
    endDate: '',
    startTime: '08:00',
    endTime: '22:00',
    reason: '',
  });

  const loadData = useCallback(async () => {
    try {
      const [venueData, courtsData] = await Promise.all([
        getVenueById(venueId),
        getVenueCourts(venueId),
      ]);
      setVenue(venueData.venue || venueData);
      setCourts(
        Array.isArray(courtsData)
          ? courtsData
          : Array.isArray(courtsData?.courts)
            ? courtsData.courts
            : [],
      );
    } catch (err) {
      toast.error('Failed to load venue data');
      navigate('/venues');
    } finally {
      setLoading(false);
    }
  }, [venueId, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadMonthBookings = useCallback(async (month) => {
    try {
      const data = await getVenueBookings(venueId, {
        fromDate: format(startOfWeek(startOfMonth(month)), 'yyyy-MM-dd'),
        toDate: format(endOfWeek(endOfMonth(month)), 'yyyy-MM-dd'),
      });
      const venueBookings = Array.isArray(data.bookings) ? data.bookings : [];
      setBookings(venueBookings.filter(
        (booking) => String(booking.status || '').toLowerCase() !== 'cancelled'
      ));
    } catch {
      setBookings([]);
    }
  }, [venueId]);

  useEffect(() => {
    loadMonthBookings(currentMonth);
  }, [currentMonth, loadMonthBookings]);

  const loadMonthBlockedSlots = useCallback(async (month) => {
    try {
      const fromDate = format(startOfWeek(startOfMonth(month)), 'yyyy-MM-dd');
      const toDate = format(endOfWeek(endOfMonth(month)), 'yyyy-MM-dd');
      const data = await getBlockedSlots(venueId, fromDate, toDate);
      setBlockedSlots(Array.isArray(data) ? data : []);
    } catch (error) {
      setBlockedSlots([]);
      toast.error(error.response?.data?.message || 'Failed to load blocked time slots');
    }
  }, [venueId]);

  useEffect(() => {
    loadMonthBlockedSlots(currentMonth);
  }, [currentMonth, loadMonthBlockedSlots]);

  function renderCalendar() {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const calStart = startOfWeek(monthStart);
    const calEnd = endOfWeek(monthEnd);

    const days = [];
    let day = calStart;
    while (day <= calEnd) {
      days.push(day);
      day = addDays(day, 1);
    }

    return days;
  }

  async function handleBlockTime(e) {
    e.preventDefault();
    if (blocking) return;
    if (!blockForm.courtId || !blockForm.date || !blockForm.endDate) {
      toast.error('Please select a court, start date and end date');
      return;
    }
    const dayCount = differenceInCalendarDays(parseISO(blockForm.endDate), parseISO(blockForm.date)) + 1;
    if (!Number.isFinite(dayCount) || dayCount < 1 || dayCount > 366) {
      toast.error('Choose an end date on or after the start date, up to 366 days.'); return;
    }
    if (!blockForm.startTime || !blockForm.endTime || blockForm.endTime === blockForm.startTime) {
      toast.error('Start time and end time cannot be the same');
      return;
    }

    setBlocking(true);
    try {
      await blockCourt(blockForm.courtId, {
        date: blockForm.date,
        endDate: blockForm.endDate,
        startTime: blockForm.startTime,
        endTime: blockForm.endTime,
        reason: blockForm.reason,
      });
      toast.success(dayCount === 1 ? 'Time slot blocked' : `Time slots blocked across ${dayCount} days`);
      await loadMonthBlockedSlots(currentMonth);
      setBlockModal(null);
      setBlockForm({
        courtId: '',
        date: '',
        endDate: '',
        startTime: '08:00',
        endTime: '22:00',
        reason: '',
      });
    } catch (err) {
      const message = err.response?.data?.message
        || (err.response?.status === 403
          ? 'You do not have permission to manage this court.'
          : 'Failed to block time slot');
      toast.error(message);
    } finally { setBlocking(false); }
  }

  function openBlockModal(date) {
    setBlockForm((prev) => ({
      ...prev,
      date: format(date, 'yyyy-MM-dd'),
      endDate: format(date, 'yyyy-MM-dd'),
      courtId: courts[0]?._id || courts[0]?.id || '',
    }));
    setBlockModal(true);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  const days = renderCalendar();
  const blockDayCount = blockForm.date && blockForm.endDate
    ? differenceInCalendarDays(parseISO(blockForm.endDate), parseISO(blockForm.date)) + 1 : 0;
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const bookingsForDay = (day) =>
    bookings
      .filter((b) => {
        const bDate = new Date(b.startTime || b.start || b.date);
        return isSameDay(bDate, day);
      })
      .sort((first, second) =>
        new Date(first.startTime || first.start) - new Date(second.startTime || second.start));
  const selectedDateBookings = bookingsForDay(selectedDate);
  const blocksForDay = (day) => blockedSlots
    .filter((slot) => String(slot.date || '').slice(0, 10) === format(day, 'yyyy-MM-dd'))
    .sort((first, second) => String(first.startTime).localeCompare(String(second.startTime)));
  const selectedDateBlocks = blocksForDay(selectedDate);

  const formatBlockedTime = (value, isEnd = false) => {
    const [hourText = '0', minuteText = '0'] = String(value || '').split(':');
    const hour = Number(hourText);
    const minute = Number(minuteText);
    if (isEnd && hour === 23 && minute === 59) return '12:00 AM';
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${String(minute).padStart(2, '0')} ${period}`;
  };

  async function handleUnblock(slot) {
    if (!window.confirm(`Remove the blocked period for ${slot.courtName}?`)) return;
    setRemovingBlockId(slot.id);
    try {
      await deleteBlockedSlot(slot.id);
      toast.success('Time slot unblocked');
      await loadMonthBlockedSlots(currentMonth);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to unblock time slot');
    } finally {
      setRemovingBlockId(null);
    }
  }

  function changeMonth(nextMonth) {
    setCurrentMonth(nextMonth);
    setSelectedDate(startOfMonth(nextMonth));
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate(-1)} className="btn-secondary">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </button>
        <div>
          <h1 className="text-2xl font-bold text-[#1F2937]">Availability Calendar</h1>
          <p className="text-sm text-[#64748B]">{venue?.name || 'Venue'} — Manage availability</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="card">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#CBD5E1]">
              <button
                onClick={() => changeMonth(subMonths(currentMonth, 1))}
                className="btn-secondary p-1.5"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <h2 className="text-lg font-semibold text-[#1F2937]">
                {format(currentMonth, 'MMMM yyyy')}
              </h2>
              <button
                onClick={() => changeMonth(addMonths(currentMonth, 1))}
                className="btn-secondary p-1.5"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4">
              <div className="grid grid-cols-7 gap-1">
                {dayNames.map((name) => (
                  <div
                    key={name}
                    className="text-center text-xs font-medium text-[#64748B] py-2"
                  >
                    {name}
                  </div>
                ))}
                {days.map((day, idx) => {
                  const isCurrentMonth = isSameMonth(day, currentMonth);
                  const isSelected = isSameDay(day, selectedDate);
                  const isToday = isSameDay(day, new Date());
                  const dayBookings = bookingsForDay(day);
                  const dayBlocks = blocksForDay(day);

                  return (
                    <button
                      key={idx}
                      onClick={() => setSelectedDate(day)}
                      className={`
                        relative p-2 text-sm rounded-lg transition-colors min-h-[80px]
                        ${!isCurrentMonth ? 'text-[#64748B]' : 'text-[#64748B]'}
                        ${isSelected ? 'bg-primary-50 border-2 border-primary-500' : 'border-2 border-transparent hover:bg-[#F9FAFB]'}
                        ${isToday && !isSelected ? 'border-primary-200' : ''}
                      `}
                    >
                      <span className="font-medium">{format(day, 'd')}</span>
                      {dayBookings.length > 0 && (
                        <span className="block mt-1 text-xs text-green-600 font-medium">
                          {dayBookings.length} booking{dayBookings.length !== 1 ? 's' : ''}
                        </span>
                      )}
                      {dayBlocks.length > 0 && (
                        <span className="block mt-1 text-xs text-amber-700 font-medium">
                          {dayBlocks.length} blocked period{dayBlocks.length !== 1 ? 's' : ''}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="card">
            <div className="px-6 py-4 border-b border-[#CBD5E1]">
              <h3 className="text-lg font-semibold text-[#1F2937]">
                {format(selectedDate, 'MMM d, yyyy')}
              </h3>
            </div>
            <div className="p-4 space-y-2">
              <button
                onClick={() => openBlockModal(selectedDate)}
                className="btn-secondary w-full justify-center"
              >
                <Lock className="h-4 w-4 mr-2" />
                Block Time Slots
              </button>

              <h4 className="mt-4 mb-2 text-sm font-medium text-[#64748B]">Blocked periods</h4>
              {selectedDateBlocks.length === 0 ? (
                <p className="rounded-lg bg-[#F9FAFB] py-3 text-center text-sm text-[#64748B]">No blocked periods</p>
              ) : (
                <div className="space-y-2">
                  {selectedDateBlocks.map((slot) => (
                    <div key={slot.id} className="flex items-start justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                      <div>
                        <p className="text-sm font-semibold text-[#1F2937]">{slot.courtName}</p>
                        <p className="mt-0.5 text-xs font-medium text-amber-800">{formatBlockedTime(slot.startTime)} – {formatBlockedTime(slot.endTime, true)}</p>
                        {slot.reason && <p className="mt-1 text-xs text-[#64748B]">{slot.reason}</p>}
                      </div>
                      <button type="button" onClick={() => handleUnblock(slot)} disabled={removingBlockId === slot.id} className="rounded-lg p-2 text-red-600 hover:bg-red-50 disabled:opacity-50" title="Unblock this period">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <h4 className="text-sm font-medium text-[#64748B] mt-4 mb-2">Bookings</h4>
              {selectedDateBookings.length === 0 ? (
                <p className="text-sm text-[#64748B] text-center py-4">No bookings on this day</p>
              ) : (
                <div className="space-y-2">
                  {selectedDateBookings.map((booking) => (
                    <div
                      key={booking._id || booking.id}
                      className="p-3 rounded-lg bg-white cursor-pointer hover:bg-[#F9FAFB]"
                      onClick={() => navigate(`/bookings/${booking._id || booking.id}`)}
                    >
                      <p className="text-sm font-medium text-[#1F2937]">
                        {booking.playerName || booking.player?.name}
                      </p>
                      <p className="mt-1 flex items-center gap-1 text-xs font-medium text-[#64748B]">
                        <CalendarIcon className="h-3.5 w-3.5" />
                        {format(new Date(booking.startTime || booking.start), 'MMM d, yyyy')}
                      </p>
                      <p className="mt-0.5 text-xs text-[#64748B]">
                        {format(new Date(booking.startTime || booking.start), 'h:mm a')} -{' '}
                        {format(new Date(booking.endTime || booking.end), 'h:mm a')}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {blockModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#264D59] bg-opacity-50 p-4">
          <div role="dialog" aria-modal="true" aria-labelledby="block-time-title" className="max-h-[90vh] overflow-y-auto w-full max-w-md bg-white rounded-xl shadow-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#CBD5E1]">
              <h3 id="block-time-title" className="text-lg font-semibold text-[#1F2937]">Block Time Slots</h3>
              <button aria-label="Close block time form" disabled={blocking} onClick={() => setBlockModal(false)} className="text-[#64748B] hover:text-[#64748B]">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleBlockTime} className="p-6 space-y-4">
              <fieldset disabled={blocking} className="space-y-4">
              <div>
                <label htmlFor="block-court" className="block text-sm font-medium text-[#64748B]">Court</label>
                <select id="block-court"
                  value={blockForm.courtId}
                  onChange={(e) =>
                    setBlockForm((prev) => ({ ...prev, courtId: e.target.value }))
                  }
                  className="input-field mt-1"
                >
                  <option value="">Select court</option>
                  {courts.map((court) => (
                    <option key={court._id || court.id} value={court._id || court.id}>
                      {court.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="block-start-date" className="block text-sm font-medium text-[#64748B]">Start date</label>
                <input
                  id="block-start-date" type="date" required
                  value={blockForm.date}
                  onChange={(e) =>
                    setBlockForm((prev) => ({ ...prev, date: e.target.value, endDate: !prev.endDate || prev.endDate < e.target.value ? e.target.value : prev.endDate }))
                  }
                  className="input-field mt-1"
                />
              </div>
              <div>
                <label htmlFor="block-end-date" className="block text-sm font-medium text-[#64748B]">End date (inclusive)</label>
                <input id="block-end-date" type="date" required value={blockForm.endDate} min={blockForm.date || undefined}
                  max={blockForm.date ? format(addDays(parseISO(blockForm.date), 365), 'yyyy-MM-dd') : undefined}
                  onChange={e => setBlockForm(prev => ({ ...prev, endDate: e.target.value }))} className="input-field mt-1" />
              </div>
              <p className="text-xs text-[#64748B]">The same time window applies every day in this range, in Philippine time. Use the same start and end date to block one day. Maximum 366 days.</p>
              {blockDayCount >= 1 && blockDayCount <= 366 && <p role="status" className="rounded-lg bg-amber-50 p-3 text-sm">Block {blockDayCount} day{blockDayCount === 1 ? '' : 's'}: {blockForm.date} through {blockForm.endDate}. Existing booking or Open Play conflicts stop the whole request.</p>}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="block-start-time" className="block text-sm font-medium text-[#64748B]">Start Time</label>
                  <input
                    id="block-start-time" type="time" required
                    value={blockForm.startTime}
                    onChange={(e) =>
                      setBlockForm((prev) => ({ ...prev, startTime: e.target.value }))
                    }
                    className="input-field mt-1"
                  />
                </div>
                <div>
                  <label htmlFor="block-end-time" className="block text-sm font-medium text-[#64748B]">End Time</label>
                  <input
                    id="block-end-time" type="time" required
                    value={blockForm.endTime}
                    onChange={(e) =>
                      setBlockForm((prev) => ({ ...prev, endTime: e.target.value }))
                    }
                    className="input-field mt-1"
                  />
                </div>
              </div>
              <p className="text-xs text-[#64748B]">Choose an end time earlier than the start time to block across midnight on each date. The final overnight block ends the day after the selected end date.</p>
              <div>
                <label htmlFor="block-reason" className="block text-sm font-medium text-[#64748B]">Reason</label>
                <input
                  id="block-reason" type="text"
                  value={blockForm.reason}
                  onChange={(e) =>
                    setBlockForm((prev) => ({ ...prev, reason: e.target.value }))
                  }
                  className="input-field mt-1"
                  placeholder="Maintenance, holiday, etc."
                />
              </div>
              </fieldset>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  disabled={blocking} onClick={() => setBlockModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" disabled={blocking} className="btn-primary">
                  <Lock className="h-4 w-4 mr-2" />
                  {blocking ? 'Blocking…' : 'Block'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
